import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';

import type { CreateExceptionRequest, ProviderDetail } from '@platform/contracts';
import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { formatLocalDate } from '../../features/scheduling/availability-preview';
import { useEhrDirectory } from '../../features/scheduling/ehr-link-field';
import { ProviderEditor } from '../../features/scheduling/provider-editor';
import { issuesFromApiError, type ValidationIssue } from '../../features/scheduling/validation';
import { useSchedulingClient } from '../../lib/api/scheduling-client-context';
import { describeApiError } from '../../lib/session/describe-error';

export const Route = createFileRoute('/providers/$providerId')({
  component: ProviderPage,
});

/**
 * `/providers/:id` — one provider's profile, week and exceptions (`Docs/API.md` §2.7).
 *
 * Two endpoints sit behind one Save: `PATCH /admin/providers/:id` for the profile and
 * `PUT /admin/providers/:id/schedules` for the week. They are sequenced rather than
 * batched because that is what the API offers; a combined write would not change the
 * screen.
 *
 * A `400 VALIDATION_FAILED` from either is turned into the editor's own field ids
 * (`issuesFromApiError`), so a rule only the server knows — an anchor that is not its own
 * weekday, a break the server measures differently — is shown under the control that
 * caused it rather than as a sentence at the top of a long form (rules §17.5).
 */
function ProviderPage() {
  const { providerId } = Route.useParams();
  const client = useSchedulingClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [saveIssues, setSaveIssues] = useState<ValidationIssue[]>([]);
  const [exceptionError, setExceptionError] = useState<string | undefined>(undefined);

  const ehrDirectory = useEhrDirectory();
  const detail = useQuery({
    queryKey: ['provider', providerId],
    queryFn: () => client.getProvider(providerId),
  });
  const types = useQuery({
    queryKey: ['appointment-types'],
    queryFn: () => client.listAppointmentTypes(),
  });
  const settings = useQuery({
    queryKey: ['scheduling-settings'],
    queryFn: () => client.getSettings(),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['provider', providerId] });
    await queryClient.invalidateQueries({ queryKey: ['providers'] });
    /* The week that was just saved is an input to the preview (API.md §2.7). */
    await queryClient.invalidateQueries({ queryKey: ['availability-preview'] });
  };

  const save = useMutation({
    mutationFn: async (draft: ProviderDetail) => {
      await client.updateProvider(providerId, {
        displayName: draft.provider.displayName,
        credentials: draft.provider.credentials,
        npi: draft.provider.npi,
        statesLicensed: draft.provider.statesLicensed,
        appointmentTypeIds: draft.provider.appointmentTypeIds,
        telehealthUrl: draft.provider.telehealthUrl,
        ehrExternalId: draft.provider.ehrExternalId,
        isActive: draft.provider.isActive,
      });
      return client.saveSchedule(providerId, {
        /*
         * `provider_schedules.effective_from`: the new week applies from today. The
         * superseded rows keep their `effective_to`, so availability already computed for
         * past dates stays explainable (API.md §2.7).
         */
        effectiveFrom: formatLocalDate(new Date().getTime(), settings.data?.timeZone ?? 'UTC'),
        days: draft.schedule,
        /*
         * Anchors, not parity: `{ weekday, startDateLocal, reason }` is what the engine
         * reads (Q-T22, SCHEDULING_UI.md §5.1). A rule the editor has just added has an
         * id it made up, which the server replaces with the row's own.
         */
        alternateWeeks: draft.alternateWeeks,
        defaults: draft.defaults,
      });
    },
    onSuccess: async () => {
      setSaveError(undefined);
      setSaveIssues([]);
      toast({ tone: 'success', message: 'Provider saved.' });
      await refresh();
    },
    onError: (error: unknown) => {
      setSaveError(describeApiError(error));
      setSaveIssues(issuesFromApiError(error));
    },
  });

  const addException = useMutation({
    mutationFn: (input: CreateExceptionRequest) => client.addException(providerId, input),
    onSuccess: async () => {
      setExceptionError(undefined);
      toast({ tone: 'success', message: 'Exception added.' });
      await refresh();
    },
    onError: (error: unknown) => {
      setExceptionError(describeApiError(error));
    },
  });

  const removeException = useMutation({
    mutationFn: (exceptionId: string) => client.removeException(providerId, exceptionId),
    onSuccess: async () => {
      toast({ tone: 'success', message: 'Exception removed.' });
      await refresh();
    },
    onError: (error: unknown) => {
      setExceptionError(describeApiError(error));
    },
  });

  if (detail.isPending || types.isPending || settings.isPending) {
    return (
      <>
        <PageHeader title="Provider" />
        <Card className="p-6">
          <Spinner label="Loading provider" />
        </Card>
      </>
    );
  }

  if (detail.isError || types.isError || settings.isError) {
    return (
      <>
        <PageHeader title="Provider" />
        <Alert tone="error">This provider could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    /*
     * `key` remounts the editor when fresh data arrives, so its draft restarts from what
     * the server has. That only happens after one of this page's own mutations — the query
     * client does not refetch on focus and nothing else invalidates these keys — so it
     * cannot discard an edit in progress.
     *
     * `dataUpdatedAt` doubles as the clock: it is when this data was fetched, which is what
     * "today" should mean here, and it keeps the component free of an ambient clock
     * (TESTING.md §2.1).
     */
    <ProviderEditor
      key={detail.dataUpdatedAt}
      detail={detail.data}
      appointmentTypes={types.data}
      statesServed={settings.data.statesServed}
      today={formatLocalDate(detail.dataUpdatedAt, settings.data.timeZone)}
      saving={save.isPending}
      saveError={saveError}
      ehrDirectory={ehrDirectory}
      serverIssues={saveIssues}
      exceptionError={exceptionError}
      eyebrow={
        <Link to="/providers" className="text-sm text-primary-accessible hover:underline">
          Providers
        </Link>
      }
      onSave={(draft) => {
        save.mutate(draft);
      }}
      onAddException={(input) => {
        addException.mutate(input);
      }}
      onRemoveException={(exceptionId) => {
        removeException.mutate(exceptionId);
      }}
    />
  );
}
